<script setup lang="ts">
import { computed } from "vue";
import dayjs from "dayjs";
import { Line } from "vue-chartjs";
import { PREVIOUS_YEARS_COLORS } from "@/modules/base_price/utils/metricColors";
import { ChartData, ChartOptions } from "chart.js";
import { formatNumber } from "chartjs-graph";
import BasePricePreviousYearAverageMonthDTO = Modules.BasePrice.Data.PreviousYear.BasePricePreviousYearAverageMonthDTO;

/**
 * A figure that is already an average per month — a CPM, a fill rate — as
 * three lines: the year before the plan, the one before that, and the two
 * combined. Unlike PlanPreviousYearsChart these are not shares of a year, so
 * nothing adds up to anything; `format` says how to read the axis.
 *
 * Identity never rests on colour alone: the legend names each line, the
 * average is dotted, and the values sit in a table under it.
 */
const props = defineProps<{
	months: BasePricePreviousYearAverageMonthDTO[];
	/** The years the "current" and "previous" figures are for, as their lines' labels. */
	currentYear: number;
	previousYear: number;
	/** Euros for a CPM, a 0-100 percentage for a fill rate. */
	format: "money" | "percent";
}>();

/**
 * The three series, in a fixed order that also fixes their colour — the same
 * PREVIOUS_YEARS_COLORS and the same dotted average as PlanPreviousYearsChart,
 * so the dialogs read alike.
 */
const SERIES = computed(() => [
	{ key: "current_year", label: String(props.currentYear), color: PREVIOUS_YEARS_COLORS.currentYear, dash: [] as number[] },
	{ key: "previous_year", label: String(props.previousYear), color: PREVIOUS_YEARS_COLORS.previousYear, dash: [] as number[] },
	{ key: "average", label: "Average", color: PREVIOUS_YEARS_COLORS.average, dash: [1, 5] },
] as const);

/**
 * The predicted-average colour, for the average's months that are not a mean
 * of the two years but the previous year carried forward by the trend: a
 * month that has not fully passed, or that has no CPM yet. The same rule as
 * PreviousYearDistributionService::distributeAvg().
 */
const PROJECTED_COLOR = PREVIOUS_YEARS_COLORS.averagePredicted;

const isProjected = (index: number) => {
	const month = props.months[index];

	return !!month && (!month.is_completed || month.current_year === 0);
};

/** Short month names, January first — the rows arrive in calendar order. */
const monthLabels = computed(() =>
	props.months.map((_, i) => dayjs().month(i).format("MMM"))
);

const chartData = computed<ChartData<"line">>(() => ({
	labels: monthLabels.value,
	datasets: SERIES.value.map((series) => ({
		label: series.label,
		data: props.months.map((month) => month[series.key]),
		borderColor: series.color,
		backgroundColor: series.color,
		...(series.key === "average"
			? {
					// A segment takes the colour of the month it leads into.
					segment: {
						borderColor: (ctx: { p1DataIndex: number }) =>
							isProjected(ctx.p1DataIndex) ? PROJECTED_COLOR : undefined,
					},
					pointBackgroundColor: props.months.map((_, i) =>
						isProjected(i) ? PROJECTED_COLOR : series.color
					),
				}
			: {}),
		borderWidth: 2,
		borderDash: [...series.dash],
		// Round caps turn the short dashes into dots.
		borderCapStyle: "round",
		pointRadius: 4,
		pointHoverRadius: 6,
		// A ring in the surface colour (variables.module.scss: color-surface)
		// keeps overlapping points apart; canvas can't read CSS variables.
		pointBorderColor: "#fbfcfd",
		pointBorderWidth: 2,
		tension: 0.25,
	})),
}));

const formatValue = (value: number) =>
	props.format === "money" ? formatNumber(value, "money") : `${value.toFixed(1)}%`;

const options = computed<ChartOptions<"line">>(() => ({
	responsive: true,
	maintainAspectRatio: false,
	animation: false,
	// Hovering anywhere in a month shows all three lines for that month.
	interaction: { mode: "index", intersect: false },
	plugins: {
		legend: {
			position: "top",
			align: "end",
			labels: { usePointStyle: true, pointStyle: "line", boxWidth: 24, color: "#2e3239" },
		},
		tooltip: {
			callbacks: {
				label: (item) => {
					const month = props.months[item.dataIndex];
					const note =
						item.datasetIndex === 0 && month && !month.is_completed && month.current_year > 0
							? " (so far)"
							: item.datasetIndex === 2 && isProjected(item.dataIndex)
								? " (projected)"
								: "";

					return ` ${item.dataset.label}: ${formatValue(Number(item.raw))}${note}`;
				},
			},
		},
		// Registered globally elsewhere in the app; nothing here wants it.
		datalabels: { display: false },
		// Likewise global once anything imports it: these lines show what
		// earlier years did, so their points are not something to drag.
		dragData: false,
	},
	scales: {
		x: {
			grid: { display: false },
			ticks: { color: "#6b7280" },
		},
		y: {
			beginAtZero: true,
			border: { display: false },
			grid: { color: "rgba(46, 50, 57, 0.08)" },
			ticks: { color: "#6b7280", callback: (value) => formatValue(Number(value)) },
		},
	},
}));
</script>

<template>
	<div>
		<div class="chart-box">
			<Line :data="chartData" :options="options" />
		</div>
		<div
			v-if="months.some((_, i) => isProjected(i))"
			class="d-flex align-center justify-end ga-1 text-caption text-medium-emphasis"
		>
			<span class="legend-line legend-line--dotted" :style="{ borderColor: PROJECTED_COLOR }" />
			Average projected from {{ previousYear }}, where {{ currentYear }} is not final yet
		</div>

		<v-table density="compact" class="values-table mt-3">
			<thead>
				<tr>
					<th>Month</th>
					<th v-for="series in SERIES" :key="series.key" class="text-right">
						<span class="d-inline-flex align-center ga-1">
							<span
								class="legend-line"
								:class="{ 'legend-line--dotted': series.dash.length }"
								:style="{ borderColor: series.color }"
							/>
							{{ series.label }}
						</span>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="(month, i) in months" :key="i">
					<td>{{ monthLabels[i] }}</td>
					<td v-for="series in SERIES" :key="series.key" class="text-right text-no-wrap">
						{{ formatValue(month[series.key]) }}
						<!-- What a month still running has done so far, not its final figure. -->
						<span
							v-if="series.key === 'current_year' && !month.is_completed && month.current_year > 0"
							class="values-note"
						>
							so far
						</span>
					</td>
				</tr>
			</tbody>
		</v-table>
	</div>
</template>

<style scoped lang="scss">
.chart-box {
	position: relative;
	height: 280px;
}

.values-table {
	font-size: 12px;
	font-variant-numeric: tabular-nums;
}

.values-table :deep(th),
.values-table :deep(td) {
	height: 26px !important;
	padding: 0 8px !important;
}

.values-note {
	margin-left: 4px;
	color: rgba(var(--v-theme-on-surface), 0.6);
}

.legend-line {
	width: 18px;
	height: 0;
	border-top: 2px solid;
	display: inline-block;
}

.legend-line--dotted {
	border-top-style: dotted;
}
</style>
